import {
  DineInRound,
  DineInRoundItem,
  DineInSession,
} from "../../../interfaces/DineInObject";
import {
  findOpenRound,
  isPlacedRound,
  lineIdentityKey,
} from "../../../services/helpers/DineInSummaryHelper";

// ─── Dine In — diner-facing feed ──────────────────────────────────────────
// Turns a session into the chronological row list the diner's table screen
// renders. Deliberately narrow: everything that is arithmetic rather than
// layout (open-round lookup, plate counts, bill totals, per-round summaries)
// now lives in services/helpers/DineInSummaryHelper.ts, because the kitchen
// board needs the same numbers and two copies would eventually disagree.

/**
 * One row of the diner feed, ready to render.
 *
 * `item` rows are DISPLAY groups, not raw store entries — see
 * groupFeedItemsForDisplay for why, and for how to get back to the underlying
 * items when an action needs them.
 */
export type DineInFeedRow =
  | { type: "sessionOpened"; timestamp: string }
  | { type: "roundClosed"; roundId: string; roundNumber: number; timestamp: string }
  | { type: "roundOpen"; roundNumber: number }
  | ({ type: "item" } & DineInDisplayItem);

/** One visually-merged line in the feed: N of the same dish, same person, same status. */
export interface DineInDisplayItem {
  /** Unique by construction — it IS the group identity. Safe as a React key. */
  key: string;
  roundId: string;
  productId: string;
  variantId?: string;
  /** Summed across every raw entry folded into this row. */
  quantity: number;
  addedBy: string;
  /** Earliest addedAt in the group, so ordering matches when it first appeared. */
  addedAt: string;
  /** Normalised — every entry in a group shares it, that's part of the key. */
  status: string;
  /** The kitchen's note, when it gave one. Also part of the key, so two
   *  rejections with different reasons never merge into one line showing only
   *  the first reason. */
  statusNote?: string;
  /**
   * itemKey of each raw DineInRoundItem folded into this row. A per-item
   * action (accept/reject one unit) must expand back out through these rather
   * than acting on the group, which has no server-side existence.
   */
  sourceItemKeys: string[];
}

/**
 * Merges raw items into readable lines WITHOUT touching what's in the store.
 *
 * The store deliberately keeps one entry per raw backend row so per-item
 * status survives (see DineInRoundItem's comment). Rendering that directly
 * would show "Dosa ×1" three times where the diner used to see "Dosa ×3", so
 * the merge happens here, at display time, and only across entries identical
 * in product, person AND status. Two dosas the kitchen has served and one it
 * hasn't stay on separate lines — which is the entire point of not
 * aggregating in the converter.
 */
export function groupFeedItemsForDisplay(items: DineInRoundItem[]): DineInDisplayItem[] {
  const groups = new Map<string, DineInDisplayItem>();

  for (const item of items) {
    const status = item.status ?? "";
    const note = item.statusNote ?? "";
    const key = lineIdentityKey(item, item.roundId, item.addedBy, status, note);
    const existing = groups.get(key);

    if (existing) {
      existing.quantity += item.quantity && item.quantity > 0 ? item.quantity : 1;
      existing.sourceItemKeys.push(item.itemKey);
      if (item.addedAt && (!existing.addedAt || item.addedAt < existing.addedAt)) {
        existing.addedAt = item.addedAt;
      }
      continue;
    }

    groups.set(key, {
      key,
      roundId: item.roundId,
      productId: item.productId,
      variantId: item.variantId,
      quantity: item.quantity && item.quantity > 0 ? item.quantity : 1,
      addedBy: item.addedBy,
      addedAt: item.addedAt,
      status,
      statusNote: item.statusNote,
      sourceItemKeys: [item.itemKey],
    });
  }

  return Array.from(groups.values());
}

/**
 * Flattens session + rounds + roundItems into one chronological feed: an
 * opening marker, then each round's items followed by a "Round N ordered"
 * divider once that round closes. Round 1 doesn't get a "Round 1 open" tag
 * (the session-opened row already marks the start of it), but it DOES get
 * the closed divider like every other round — a round finishing should
 * always be visible, no exceptions.
 *
 * The backend only creates a round's record once an item lands in it, so
 * there's a real gap between the previous round closing and the next one
 * getting its first item — during that window `rounds` has no entry for it
 * yet. Rather than showing nothing there, a "Round N open" row is
 * synthesized the instant the latest known round closes with no open round
 * behind it, so that state is visible immediately instead of waiting on the
 * first item to confirm it.
 */
export function buildDineInFeedRows(
  session: DineInSession,
  rounds: DineInRound[],
  roundItems: DineInRoundItem[]
): DineInFeedRow[] {
  const rows: DineInFeedRow[] = [{ type: "sessionOpened", timestamp: session.createdAt }];

  const sortedRounds = [...rounds].sort((a, b) => a.roundNumber - b.roundNumber);
  for (const round of sortedRounds) {
    // Open means DRAFT — still the table's to edit. Every other status
    // (REQUESTED and the kitchen's verdicts) means it's been placed and gets
    // the "Round N ordered" divider.
    const isOpen = !isPlacedRound(round.status);
    if (isOpen && round.roundNumber > 1) {
      rows.push({ type: "roundOpen", roundNumber: round.roundNumber });
    }

    const displayItems = groupFeedItemsForDisplay(
      roundItems.filter((item) => item.roundId === round.roundId)
    ).sort((a, b) => a.addedAt.localeCompare(b.addedAt));

    for (const item of displayItems) {
      rows.push({ type: "item", ...item });
    }

    if (!isOpen) {
      rows.push({
        type: "roundClosed",
        roundId: round.roundId,
        roundNumber: round.roundNumber,
        timestamp: round.orderedAt,
      });
    }
  }

  if (sortedRounds.length > 0 && findOpenRound(sortedRounds) === null) {
    const lastRound = sortedRounds[sortedRounds.length - 1];
    rows.push({ type: "roundOpen", roundNumber: lastRound.roundNumber + 1 });
  }

  return rows;
}
